import React from 'react';
import { Card, Tag, Typography } from 'antd';
import { CalendarOutlined, FileTextOutlined, DollarOutlined, RightOutlined } from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import dayjs from 'dayjs';
import './GroupCard.css';

const { Text } = Typography;

/**
 * Formats date/label based on grouping type
 */
const formatGroupLabel = (group, groupType) => {
  if (groupType === 'month') {
    // group.key format: "2024-01" or group.label: "January 2024"
    const monthKey = group.key || group.month || group.month_key;
    if (monthKey) {
      const date = dayjs(monthKey + '-01');
      return {
        primary: date.format('MMMM'),
        secondary: date.format('YYYY'),
        full: date.format('MMMM YYYY')
      };
    }
    return {
      primary: group.label || 'Unknown',
      secondary: '',
      full: group.label || 'Unknown'
    };
  } else if (groupType === 'week') {
    // group.key format: "2024-W01" or group.label: "Week 1, 2024 (Jan 1 - Jan 7)"
    if (group.label) {
      return {
        primary: group.label.split('(')[0].trim(), // "Week 1, 2024"
        secondary: group.label.includes('(') ? `(${group.label.split('(')[1]}` : '',
        full: group.label
      };
    }
    return {
      primary: group.key || 'Unknown Week',
      secondary: '',
      full: group.key || 'Unknown Week'
    };
  } else if (groupType === 'year') {
    // group.key format: "2024"
    return {
      primary: group.key || group.label || 'Unknown',
      secondary: '',
      full: group.key || group.label || 'Unknown'
    };
  } else {
    // encounter_date
    const date = dayjs(group.encounter_date || group.key);
    return {
      primary: date.format('D'),
      secondary: date.format('MMM YYYY'),
      full: date.format('MMMM D, YYYY')
    };
  }
};

/**
 * GroupCard - Generic card component for displaying grouped claims (Month, Week, Year, or Encounter Date)
 * 
 * @param {Object} props
 * @param {Object} props.group - Group data { key, label, claims_count, total_cost, ... }
 * @param {string} props.groupType - Type of grouping: 'month', 'week', 'year', 'encounter_date'
 * @param {Function} props.onClick - Click handler
 */
const GroupCard = ({ group, groupType = 'encounter_date', onClick }) => {
  if (!group) return null;

  const labelInfo = formatGroupLabel(group, groupType);
  const claimsCount = group.claims_count || group.claimsCount || 0;
  const totalCost = group.total_cost || group.totalCost || 0;
  const batchesCount = group.batches_count || group.batchesCount; // For months/weeks

  return (
    <Card
      className={`group-card group-card--${groupType}`}
      hoverable
      onClick={onClick}
      style={{
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      <div className="group-card__content">
        {/* Label section - prominent */}
        <div className="group-card__label">
          {groupType === 'encounter_date' ? (
            <>
              <div className="group-card__label-primary">{labelInfo.primary}</div>
              <div className="group-card__label-secondary">{labelInfo.secondary}</div>
            </>
          ) : (
            <>
              <div className="group-card__label-primary">{labelInfo.primary}</div>
              {labelInfo.secondary && (
                <div className="group-card__label-secondary">{labelInfo.secondary}</div>
              )}
            </>
          )}
        </div>

        {/* Stats section */}
        <div className="group-card__stats">
          <div className="group-card__claims-count">
            <FileTextOutlined style={{ marginRight: '4px' }} />
            <Tag color="blue" style={{ margin: 0 }}>
              {claimsCount}
            </Tag>
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '4px' }}>
              {claimsCount === 1 ? 'claim' : 'claims'}
            </Text>
            {batchesCount !== undefined && (
              <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                in {batchesCount} {batchesCount === 1 ? 'batch' : 'batches'}
              </Text>
            )}
          </div>
          <div className="group-card__total-cost">
            <DollarOutlined style={{ marginRight: '4px' }} />
            <CurrencyFormat
              value={totalCost || 0}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'₦'}
              decimalScale={2}
            />
          </div>
        </div>

        {/* Action indicator */}
        <div className="group-card__action">
          <RightOutlined />
        </div>
      </div>
    </Card>
  );
};

export default GroupCard;

